"use client"

import { useEffect } from "react"
import { numberFormatter } from "../../lib/util"
import { useSelector } from "react-redux"

export default function InvoiceChangeHandler() {
  const currency = useSelector((state) => state.invoice.currency)

  useEffect(() => {
    const USER_LOCALE = navigator.language || navigator.userLanguage // e.g., "en-US", "en-IN"
    const ssgRows = document.querySelectorAll(".invoice-row-ssg > .grid")
    if (!ssgRows.length) return

    const cleanups = []

    const calcAllSubtotal = (rowType) => Array.from(rowType).reduce((acc, row, index) => { 
      const rowTax = row.querySelector('[id^="item-tax"]')
      const rowQtyEl = row.querySelector('[id^="item-quantity"]')
      const rowUnitPriceEl = row.querySelector('[id^="item-unit-price"]')
      const taxTypeInput = row.querySelector('[id^="tax-type-ssg"]')
      
      const tax = parseFloat(rowTax?.value) || 0
      const qty = parseFloat(rowQtyEl?.value) || 0
      const unitPrice = parseFloat(rowUnitPriceEl?.value) || 0
      const isPercentage = taxTypeInput?.value === 'percentage'
      
      // Calculate tax based on type
      const calculatedTax = isPercentage ? (qty * unitPrice * tax / 100) : tax
      
      acc.tax += calculatedTax
      acc.subTotal += (qty * unitPrice)
      return acc
    }, {tax: 0, subTotal: 0})

    function calculateAndUpdate(qty, price, tax, subtotalEl, invoiceFinaltotalEl, invoiceFinalTaxEl, invoiceFinalPriceEl, taxTypeInput, taxFixedValueEl) {
      const csrRows = document.querySelectorAll(".invoice-row-csr > .grid")
      const q = parseFloat(qty.value) || 0
      const p = parseFloat(price.value) || 0
      const t = parseFloat(tax.value) || 0
      const isPercentage = taxTypeInput?.value === 'percentage'
      
      // Calculate tax based on type
      const calculatedTax = isPercentage ? (q * p * t / 100) : t
      const subtotal = q > 0 ? (q * p + calculatedTax).toFixed(2) : 0
      const formattedSubTotal = numberFormatter(subtotal, USER_LOCALE)
      const formattedTax = `${currency} ${numberFormatter(calculatedTax, USER_LOCALE)}`

      subtotalEl.textContent = formattedSubTotal
      taxFixedValueEl.textContent = formattedTax
      
      // calculate total of tax and subtotal across all the items
      const ssgCalculatedObj = calcAllSubtotal(ssgRows)
      const csrCalculatedObj = calcAllSubtotal(csrRows)
      
      const summarySubtotal = ssgCalculatedObj.subTotal + csrCalculatedObj.subTotal
      const totalTax = ssgCalculatedObj.tax + csrCalculatedObj.tax
      const finalPrice = totalTax + summarySubtotal
      
      invoiceFinaltotalEl.textContent = numberFormatter(summarySubtotal, USER_LOCALE)
      invoiceFinalTaxEl.textContent = numberFormatter(totalTax, USER_LOCALE)
      invoiceFinalPriceEl.textContent = numberFormatter(finalPrice, USER_LOCALE)
    }

    ssgRows.forEach((row, index) => {
      const qty = document.getElementById(`item-quantity-ssg${index}`)
      const price = document.getElementById(`item-unit-price-ssg${index}`)
      const tax = document.getElementById(`item-tax-ssg${index}`)
      const subtotalEl = document.getElementById(`subtotal-ssg${index}`)
      const taxTypeInput = document.getElementById(`tax-type-ssg${index}`)
      const percentageBtn = document.getElementById(`tax-toggle-percentage-ssg${index}`)
      const fixedBtn = document.getElementById(`tax-toggle-fixed-ssg${index}`)
      const taxFixedValueEl = document.getElementById(`tax-fixed-value-ssg${index}`)
      // summary related elements
      const invoiceFinaltotalEl = document.getElementById('invoice-final-subtotal')
      const invoiceFinalTaxEl = document.getElementById('invoice-final-tax')
      const invoiceFinalPriceEl = document.getElementById('invoice-final-price')


      if (!qty || !price || !tax || !subtotalEl || !taxTypeInput || !percentageBtn || !fixedBtn || !taxFixedValueEl) return

      const handleInputChange = () => calculateAndUpdate(qty, price, tax, subtotalEl, invoiceFinaltotalEl, invoiceFinalTaxEl, invoiceFinalPriceEl, taxTypeInput, taxFixedValueEl)
      
      const handlePercentageClick = () => {
        taxTypeInput.value = 'percentage'
        percentageBtn.classList.remove('bg-gray-200', 'text-gray-700')
        percentageBtn.classList.add('bg-blue-600', 'text-white')
        fixedBtn.classList.remove('bg-blue-600', 'text-white')
        fixedBtn.classList.add('bg-gray-200', 'text-gray-700')
        handleInputChange()
      }
      
      const handleFixedClick = () => {
        taxTypeInput.value = 'fixed'
        fixedBtn.classList.remove('bg-gray-200', 'text-gray-700')
        fixedBtn.classList.add('bg-blue-600', 'text-white')
        percentageBtn.classList.remove('bg-blue-600', 'text-white')
        percentageBtn.classList.add('bg-gray-200', 'text-gray-700')
        handleInputChange()
      }

      qty.addEventListener("input", handleInputChange)
      price.addEventListener("input", handleInputChange)
      tax.addEventListener("input", handleInputChange)
      percentageBtn.addEventListener("click", handlePercentageClick)
      fixedBtn.addEventListener("click", handleFixedClick)

      cleanups.push(() => {
        qty.removeEventListener("input", handleInputChange)
        price.removeEventListener("input", handleInputChange)
        tax.removeEventListener("input", handleInputChange)
        percentageBtn.removeEventListener("click", handlePercentageClick)
        fixedBtn.removeEventListener("click", handleFixedClick)
      })

      // Initialize button states
      if (taxTypeInput.value === 'percentage') {
        percentageBtn.classList.add('bg-blue-600', 'text-white')
        fixedBtn.classList.add('bg-gray-200', 'text-gray-700')
      } else {
        fixedBtn.classList.add('bg-blue-600', 'text-white')
        percentageBtn.classList.add('bg-gray-200', 'text-gray-700')
      }
      handleInputChange()
    })

    return () => {
      cleanups.forEach((fn) => fn())
    }
    
  }, [currency])

  return null
}
